import type { PerformanceReportSelectOptions, ViewTab } from "../types";

import { dayjs } from "../../utils/date";

/**
 * The Performance Report's concrete URL-param schema — the single place the
 * page's option sets meet the generic, page-agnostic codec in
 * `packages/ui/src/context/urlParams/`. Shared by portal and backoffice; only
 * the mapping from an origin to that host's Marketing tab (its label and hash)
 * stays per-app, since the two apps' Marketing routes differ.
 */

export const ARRAY_PARAM_KEYS = [
  "sourceType",
  "landingPage",
  "bannerGroup",
  "rebateProgram",
  "country",
  "campaign",
] as const;

export type ArrayParamKey = (typeof ARRAY_PARAM_KEYS)[number];

export const SCALAR_PARAM_KEYS = [
  "startDate",
  "endDate",
  "tab",
  "from",
] as const;

/** The "View by" tab — seedable via `?tab=` for a deep link, but not one of the
 * async-option-backed array facets above, so it's decoded separately. */
export const VIEW_TAB_VALUES: readonly ViewTab[] = [
  "source",
  "country",
  "rebate",
  // Without this entry `decodeViewTab` silently drops `?tab=campaign` and a
  // legitimate flag-on deep link lands on the default tab instead. This is a
  // pure wire-format codec with no access to feature flags — whether the tab is
  // actually *available* is authorised by `PerformanceReportProvider` against
  // the options it was given.
  "campaign",
];

export const decodeViewTab = (
  value: string | undefined,
): ViewTab | undefined =>
  VIEW_TAB_VALUES.includes(value as ViewTab) ? (value as ViewTab) : undefined;

/** Canonical wire-shaped search record for the Performance Report route —
 * every value is a plain string (comma-joined for array facets), never an
 * array. */
export type PerformanceReportSearch = Partial<Record<ArrayParamKey, string>> & {
  startDate?: string;
  endDate?: string;
  tab?: string;
  from?: string;
};

/** True when the search carries at least one array-facet filter. Used to gate
 * the deep-link options fetch (options must resolve before facet values can be
 * whitelisted) and to decide whether to seed the provider. */
export const hasArrayFilterParams = (
  search: Record<string, string | undefined>,
): boolean => ARRAY_PARAM_KEYS.some((key) => Boolean(search[key]));

/**
 * Builds the codec's per-facet whitelist from the fetched filter option sets.
 * `sourceType` is the static UI-owned enum; the others come from the async
 * filter-options API — values must be validated against the known option sets
 * before use.
 */
export const buildArrayParamWhitelists = (
  selectOptions: PerformanceReportSelectOptions,
): Record<ArrayParamKey, readonly string[]> => ({
  sourceType: selectOptions.sourceType,
  landingPage: selectOptions.landingPage.map((o) => o.value),
  bannerGroup: selectOptions.bannerGroup.map((o) => o.value),
  rebateProgram: selectOptions.rebateProgram.map((o) => o.value),
  country: selectOptions.country.map((o) => o.value),
  campaign: selectOptions.campaign.map((o) => o.value),
});

/** The same 30-day default range `PerformanceReportProvider` seeds when no
 * `initialState` is supplied — mirrored here so a deep link missing/with
 * invalid dates falls back to exactly what an un-deep-linked visit gets. */
export const defaultDateRange = (): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);
  return { from, to };
};

export const PERFORMANCE_REPORT_MIN_DATE = new Date(2025, 3, 7);

export const PERFORMANCE_REPORT_MAX_RANGE_DAYS = 365;

const clampDate = (value: Date, now: Date): Date => {
  if (dayjs(value).isBefore(PERFORMANCE_REPORT_MIN_DATE, "day")) {
    return new Date(PERFORMANCE_REPORT_MIN_DATE);
  }
  if (dayjs(value).isAfter(now, "day")) {
    return dayjs(now).startOf("day").toDate();
  }
  return value;
};

export const clampPerformanceReportRange = (
  range: { from: Date; to: Date },
  now: Date = new Date(),
): { from: Date; to: Date } => {
  const to = clampDate(range.to, now);
  const from = clampDate(range.from, now);
  if (dayjs(to).diff(from, "day") > PERFORMANCE_REPORT_MAX_RANGE_DAYS) {
    return {
      from: dayjs(to)
        .subtract(PERFORMANCE_REPORT_MAX_RANGE_DAYS, "day")
        .toDate(),
      to,
    };
  }
  return { from, to };
};
